import React, { useEffect, useState } from "react"
import GetModelQuery from "./components/GetModelQuery"
import Loading from "../../components/Loading"
import { Redirect } from "react-router"
import PowerBIEmbed from "../../components/PowerBIEmbed"
import Navigation from "./components/Navigation"
import { getActivePage, setActivePage } from "./utils"
import Upsell from "../../components/UpsellModal"
import BIError from "../../components/BIError"
import moment from "moment"
import "./model.scss"

const Model = (props) => {
  const { model, refetchQuery, didRefetch } = props
  const [rendered, setRendered] = useState(false)
  const [error, setError] = useState(false)

  const searchParams = new URLSearchParams(props.location.search)

  const applyInitialState = (bookmark, page) => {
    if (bookmark) {
      return window.report.bookmarksManager.applyState(bookmark)
    }

    if (page) {
      return setActivePage(window.report, page)
    }

    return Promise.resolve()
  }

  const isValidEmbedToken = () => {
    const { embedToken } = model

    if (!embedToken) {
      return false
    }

    if (!embedToken.token || !embedToken.id || !embedToken.expiration) {
      return false
    }

    const now = moment.utc()
    const expiration = moment.utc(embedToken.expiration)

    return now.isBefore(expiration)
  }

  useEffect(() => {
    if (!didRefetch && !isValidEmbedToken()) {
      refetchQuery()
    }
  }, [])

  const handleError = () => {
    setError(true)
  }

  const handleLoaded = () => {
    console.log("Report has loaded")
    // Sometimes the visual don't render right away even when the
    // report is "loaded" but triggering a refresh seems to help.
    window.report.refresh()
  }

  const handleRendered = () => {
    console.log("Report rendered")
    setRendered(true)
  }

  const handlePageChange = (event) => {
    searchParams.set("page", event.detail.newPage.displayName)
    props.history.replace({
      search: searchParams.toString(),
    })
  }

  useEffect(() => {
    if (window.report) {
      window.report.on("error", handleError)
      window.report.on("loaded", handleLoaded)
      window.report.on("rendered", handleRendered)

      return () => {
        window.report.off("error", handleError)
        window.report.off("loaded", handleLoaded)
        window.report.off("rendered", handleRendered)
      }
    }
  }, [])

  useEffect(() => {
    if (rendered && window.report) {
      // Only subscribe to pageChanged when rendered is true otherwise
      // we get this event before "loaded" which leads to some weird behavior
      window.report.on("pageChanged", handlePageChange)

      const bookmarkParam = searchParams.get("bookmark")
      const pageParam = searchParams.get("page")

      applyInitialState(bookmarkParam, pageParam)
        .then(() => getActivePage(window.report))
        .then((page) => {
          searchParams.set("page", page.displayName)
          searchParams.delete("bookmark")
          props.history.replace({
            search: searchParams.toString(),
          })
        })

      return () => window.report.off("pageChanged", handlePageChange)
    }
  }, [rendered])

  if (model.embedToken.token === "Not Authorized") {
    return (
      <div className="model-view">
        <Navigation model={model} rendered={rendered} />
        <Upsell />
      </div>
    )
  }

  return (
    <div className="model-view">
      <Navigation model={model} rendered={rendered} />
      {isValidEmbedToken() && !error ? (
        <PowerBIEmbed model={model} />
      ) : (
        <BIError />
      )}
    </div>
  )
}

const ModelWithData = (props) => {
  const { slug } = props.match.params
  const [didRefetch, setDidRefetch] = useState(false)

  const refetchQuery = (refetch) => () => {
    refetch()
    setDidRefetch(true)
  }

  return (
    <GetModelQuery variables={{ slug }}>
      {({ data, loading, error, refetch }) => {
        if (loading) return <Loading />
        if (error) return "An unknown error occurred trying to fetch the model."
        if (!data || !data.getModel) return <Redirect to="/not-found" />
        return (
          <Model
            model={data.getModel}
            refetchQuery={refetchQuery(refetch)}
            didRefetch={didRefetch}
            {...props}
          />
        )
      }}
    </GetModelQuery>
  )
}

export default ModelWithData
